import * as React from "react";
import { observer, inject } from "mobx-react";
import { MesoAdGroupRegionProps, MesoAdGroupRegionFTNodeOptions } from "./typings";
import { BlossomComponents } from "src/components/flexFramework/BlossomComponents/BlossomComponents";
import { normalizeOptionsData, hasRenderableChilren } from "src/components/flexFramework/FlexTemplateNode";

const MesoAdGroupRegionEventName = "adGroup";
const MesoFallbackEventName = "mesoFallback";

export const MesoAdGroupRegion: React.FC<MesoAdGroupRegionProps> = inject(
  "advertising",
  "flexModuleModelStore"
)(
  observer((props: MesoAdGroupRegionProps) => {
    const MesoAdGroupRegion: React.RefObject<JSX.IntrinsicElements["meso-ad-group"]> = React.createRef();
    const [collapsed, setCollapsed] = React.useState(false);
    const { advertising, context, blossomComponent, flexTemplateNode, flexModuleModelStore } = props;

    const options = normalizeOptionsData(
      flexTemplateNode,
      flexModuleModelStore.getModel(flexTemplateNode.id)
    ) as MesoAdGroupRegionFTNodeOptions;

    if (!options || !hasRenderableChilren(flexTemplateNode, flexModuleModelStore)) return null;
    const { mesoAdCollapse } = options;

    const removeAdLoadEventHandler = () => {
      if (MesoAdGroupRegion && MesoAdGroupRegion.current) {
        if (mesoAdCollapse) window.removeEventListener(MesoFallbackEventName, handleFallbackEvent);
        window.removeEventListener(MesoAdGroupRegionEventName, handleAdGroupEvent);
        MesoAdGroupRegion.current.removeEventListener(MesoAdGroupRegionEventName, handleAdGroupEvent);
      }
    };

    const collapseAdGroup = () => {
      setCollapsed(true);
      removeAdLoadEventHandler();
    };

    const handleAdGroupEvent = (event: CustomEvent) => {
      const details = (event && event.detail) || {};
      if (details.type === "meso-ad-group-event" && details.collapse) collapseAdGroup();
    };
    const handleFallbackEvent = (event: CustomEvent) => {
      collapseAdGroup();
    };

    React.useEffect(() => {
      /* istanbul ignore next */
      advertising?.includeAd?.();
      if (MesoAdGroupRegion && MesoAdGroupRegion.current) {
        if (mesoAdCollapse) window.addEventListener(MesoFallbackEventName, handleFallbackEvent);
        window.addEventListener(MesoAdGroupRegionEventName, handleAdGroupEvent);
        MesoAdGroupRegion.current.addEventListener(MesoAdGroupRegionEventName, handleAdGroupEvent);
      }
      return removeAdLoadEventHandler;
    }, [MesoAdGroupRegion.current]);

    /* istanbul ignore next */
    advertising?.includeAd?.();

    if (!blossomComponent || !advertising || (advertising && advertising.adBlockerEnabled) || collapsed) {
      return null;
    }

    return (
      <div>
        {mesoAdCollapse ? (
          <meso-ad-group data-testid="meso-ad-group-region" ref={MesoAdGroupRegion}>
            <BlossomComponents blossomComponent={blossomComponent} context={context} />
          </meso-ad-group>
        ) : (
          <BlossomComponents blossomComponent={blossomComponent} context={context} />
        )}
      </div>
    );
  })
);

export default MesoAdGroupRegion;
